import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { FormattedMessage } from 'react-intl';

import RefreshIcon from '@/assets/icons/refresh.svg';
import { CloseIcon } from '@/components/icons/close';
import { Button } from '@/components/ui/button';
import { Typography } from '@/components/ui/typography';
import { queryKeys } from '@/query-keys';

import { useValidateBaseFields } from './hooks/use-validate-base-fields';

interface RefreshBannerProps {
  onClose: () => void;
}

export function RefreshBanner({ onClose }: RefreshBannerProps) {
  const validateBaseFields = useValidateBaseFields();
  const queryClient = useQueryClient();
  const isRefreshing =
    useIsFetching({ queryKey: [...queryKeys.jobPosting.jobDetails] }) > 0;

  const handleRefresh = async () => {
    await queryClient.invalidateQueries({
      queryKey: [...queryKeys.jobPosting.jobDetails],
    });
    await validateBaseFields();
    onClose();
  };

  return (
    <div className="shadow-base sticky top-0 z-10 flex items-center gap-2 rounded border border-blue-600 bg-blue-50 p-2">
      <img src={RefreshIcon} alt="refresh-data" />

      <Typography variant="subtitle1" className="min-w-0 flex-1">
        <FormattedMessage
          defaultMessage="Please refetch the job details to see your updates from Hire"
          description="Hint after job details data sync: refetch to show latest from Hire"
        />
      </Typography>

      {/* Refresh button */}
      <Button
        type="button"
        variant="secondary"
        className="h-8 shrink-0 rounded-md"
        size="sm"
        onClick={() => void handleRefresh()}
      >
        {isRefreshing ? (
          <Loader2 className="animate-spin" />
        ) : (
          <img src={RefreshIcon} alt="refresh-data" />
        )}
        <FormattedMessage
          defaultMessage="Refresh now"
          description="Button to refresh job details"
        />
      </Button>

      {/* Close the banner */}
      <Button
        type="button"
        variant="icon"
        size="icon"
        onClick={onClose}
        className="flex h-6 w-6 items-center justify-center hover:!bg-blue-50"
      >
        <CloseIcon className="text-blue-600" />
      </Button>
    </div>
  );
}
